import { useMemo } from 'react'
import Places from 'constants/places'
import { HOTEL_OFFERS_LIMIT } from 'constants/charterFlight'
import type { FlightsClassTypes } from 'constants/flight'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffers from 'hooks/Offers/useOffers'
import { buildBundleOfferFilters } from '../MaldivesDirectUtils'

interface UseMaldivesDirectLightOfferListParams {
  rooms: Array<App.Occupants>
  fareClass?: FlightsClassTypes
  disabled?: boolean
  limit?: number
}

/**
 * Non-stream (light) Maldives charter offer source.
 * Used by pricing hooks that should be decoupled from streamed tile offers.
 */
export function useMaldivesDirectLightOfferList(
  params: UseMaldivesDirectLightOfferListParams,
): {
  offerList: App.OfferList
  offers: Array<App.HotelOffer>
  offersFetching: boolean
} {
  const {
    rooms,
    fareClass,
    disabled = false,
    limit = HOTEL_OFFERS_LIMIT,
  } = params

  const filters = useMemo<App.OfferListFilters | undefined>(() => {
    if (disabled || rooms.length === 0) {
      return undefined
    }

    const bundleFilters = buildBundleOfferFilters({
      destinationId: Places.Maldives.id,
      destinationName: Places.Maldives.name,
      checkIn: '', // light search source: pricing logic supplies date-specific lookups
      checkOut: '',
      rooms,
      fareClass: fareClass as App.OfferListFilters['fareClass'],
      limit,
    })

    return {
      ...bundleFilters,
      isStream: false,
      limit,
    }
  }, [disabled, rooms, fareClass, limit])

  const offerList = useOfferList(filters, {
    minRequiredOffers: limit,
    disabled: !filters,
  })

  const [offers, offersFetching] = useOffers<App.HotelOffer>(offerList.offerIds)

  return {
    offerList,
    offers,
    offersFetching,
  }
}
